import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency as formatMoney } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

/**
 * D5-D8: Unified Ads Intelligence Dashboard
 *
 * Shows TRUE ROAS (from CRM deal revenue, not platform-reported),
 * cross-platform metrics, top campaigns, and anomaly detection.
 *
 * TRUE ROAS = CRM deal revenue / ad spend
 * Platform ROAS = platform-reported revenue / ad spend (OVERCOUNTED)
 */

interface DashboardData {
  totals: {
    spendCents: number;
    trueRevenueCents: number;
    platformRevenueCents: number;
    trueRoas: number;
    platformRoas: number;
    overcountFactor: number;
    cpl: number;
    leads: number;
  };
  byPlatform: Array<{
    platform: string;
    spendCents: number;
    trueRoas: number;
    platformRoas: number;
    cpl: number;
    leads: number;
  }>;
}

interface CampaignData {
  id: string;
  externalCampaignId: string;
  platform: string;
  name: string;
  spendCents: number;
  leads: number;
  trueConversions: number;
  trueRevenueCents: number;
  trueRoas: number;
  platformRoas: number;
  cpl: number;
  ctr: number;
  lastSyncAt: string | null;
}

interface AnomalyData {
  campaignId: string;
  campaignName: string;
  platform: string;
  type: string;
  severity: "warning" | "critical";
  message: string;
  recommendation: string;
}

interface AdAccount {
  id: string;
  platform: string;
  accountName: string;
  status: string;
  lastSyncAt: string | null;
}

interface BudgetOptimizerData {
  currentDailyBudgetCents: number;
  recommendedDailyBudgetCents: number;
  currentRoas: number;
  platforms: Array<{
    platform: string;
    currentBudgetCents: number;
    recommendedBudgetCents: number;
    currentRoas: number;
    projectedRoas: number;
  }>;
  aiRecommendation: string | null;
}

type RecommendationAction = "increase" | "decrease" | "pause";

type CampaignRecommendation = {
  id: string;
  name: string;
  platform: string;
  action: RecommendationAction;
  reason: string;
  actionLabel: string;
  trueRoas: number;
  spendCents: number;
  editorHref: string;
};

const PLATFORM_COLORS: Record<string, string> = {
  meta: "bg-[var(--color-primary)]",
  google: "bg-[var(--color-destructive)]",
  tiktok: "bg-[var(--color-foreground)]",
};

const PLATFORM_NAMES: Record<string, string> = {
  meta: "Meta",
  google: "Google",
  tiktok: "TikTok",
};

function getPlatformEditorHref(platform: string, externalCampaignId: string): string {
  if (!externalCampaignId) return "/analytics/ads";

  switch (platform) {
    case "meta":
      return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?selected_campaign_ids=${encodeURIComponent(externalCampaignId)}`;
    case "google":
      return `https://ads.google.com/aw/campaigns?campaignId=${encodeURIComponent(externalCampaignId)}`;
    case "tiktok":
      return `https://ads.tiktok.com/i18n/perf/campaign?campaign_id=${encodeURIComponent(externalCampaignId)}`;
    default:
      return "/analytics/ads";
  }
}

function isOlderThanDays(value: string | null, days: number): boolean {
  if (!value) return false;
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return false;
  return (Date.now() - timestamp) >= days * 24 * 60 * 60 * 1000;
}

function buildCampaignRecommendations(campaigns: CampaignData[]): CampaignRecommendation[] {
  return campaigns
    .map((campaign) => {
      const editorHref = getPlatformEditorHref(campaign.platform, campaign.externalCampaignId);

      if (campaign.trueConversions === 0 && campaign.spendCents > 0 && isOlderThanDays(campaign.lastSyncAt, 14)) {
        return {
          id: campaign.id,
          name: campaign.name,
          platform: campaign.platform,
          action: "pause" as const,
          reason: "No true conversions in the synced 14+ day window.",
          actionLabel: "Pause campaign",
          trueRoas: campaign.trueRoas,
          spendCents: campaign.spendCents,
          editorHref,
        };
      }

      if (campaign.trueRoas > 2) {
        return {
          id: campaign.id,
          name: campaign.name,
          platform: campaign.platform,
          action: "increase" as const,
          reason: "TRUE ROAS is above 2x and supports more budget.",
          actionLabel: "Increase budget",
          trueRoas: campaign.trueRoas,
          spendCents: campaign.spendCents,
          editorHref,
        };
      }

      if (campaign.trueRoas < 1 && campaign.spendCents > 0) {
        return {
          id: campaign.id,
          name: campaign.name,
          platform: campaign.platform,
          action: "decrease" as const,
          reason: "TRUE ROAS is below 1x, so budget should be trimmed.",
          actionLabel: "Decrease budget",
          trueRoas: campaign.trueRoas,
          spendCents: campaign.spendCents,
          editorHref,
        };
      }

      return null;
    })
    .filter((recommendation): recommendation is CampaignRecommendation => recommendation !== null)
    .sort((left, right) => {
      const actionWeight: Record<RecommendationAction, number> = { pause: 3, decrease: 2, increase: 1 };
      const byAction = actionWeight[right.action] - actionWeight[left.action];
      if (byAction !== 0) return byAction;
      if (left.action === "increase") return right.trueRoas - left.trueRoas;
      return right.spendCents - left.spendCents;
    })
    .slice(0, 5);
}

function getActionBadgeClass(action: RecommendationAction): string {
  switch (action) {
    case "increase":
      return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "decrease":
      return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    case "pause":
      return "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
}

export default function AdsIntelligenceDashboard() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignData[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyData[]>([]);
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [optimizer, setOptimizer] = useState<BudgetOptimizerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, campRes, anomRes, accRes, optimizerRes] = await Promise.all([
        apiFetch<{ data: DashboardData }>("/v1/ads/dashboard"),
        apiFetch<{ data: CampaignData[] }>("/v1/ads/campaigns"),
        apiFetch<{ data: AnomalyData[] }>("/v1/ads/anomalies"),
        apiFetch<{ data: AdAccount[] }>("/v1/ads/accounts"),
        apiFetch<{ data: BudgetOptimizerData }>("/v1/ads/budget-optimizer").catch(() => null),
      ]);
      setDashboard(dashRes.data);
      setCampaigns(campRes.data);
      setAnomalies(anomRes.data);
      setAccounts(accRes.data);
      setOptimizer(optimizerRes?.data ?? null);
    } catch { /* empty state */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-24 rounded-xl" />)}
      </div>
    );
  }

  const connectedPlatforms = accounts.filter((a) => a.status === "connected");

  if (connectedPlatforms.length === 0) {
    return (
      <div>
        <h1 className="mb-6 text-2xl font-bold">Ads Intelligence</h1>
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <span className="text-4xl">📊</span>
          <h3 className="mt-3 text-lg font-semibold">Connect your ad platforms</h3>
          <p className="mt-1 text-sm text-muted-foreground">Connect Meta, Google, or TikTok Ads to see TRUE ROAS from your CRM data.</p>
          <div className="mt-4 flex justify-center gap-3">
            {["meta", "google", "tiktok"].map((p) => (
              <a key={p} href="/analytics/ads/connect" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
                Connect {PLATFORM_NAMES[p]}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const d = dashboard?.totals;
  const recommendations = buildCampaignRecommendations(campaigns);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Ads Intelligence</h1>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {connectedPlatforms.map((a) => (
            <span key={a.id} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-white ${PLATFORM_COLORS[a.platform] ?? "bg-[var(--color-muted-foreground)]"}`}>
              {PLATFORM_NAMES[a.platform] ?? a.platform}
            </span>
          ))}
        </div>
      </div>

      {/* D5: Totals bar */}
      {d && (
        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <MetricCard label="Total Spend" value={formatMoney(d.spendCents, "EUR")} />
          <MetricCard label="TRUE Revenue" value={formatMoney(d.trueRevenueCents, "EUR")} highlight />
          <MetricCard label="TRUE ROAS" value={`${d.trueRoas.toFixed(2)}x`} highlight />
          <MetricCard label="Cost Per Lead" value={formatMoney(d.cpl, "EUR")} />
        </div>
      )}

      {/* D6: TRUE vs Platform ROAS */}
      {d && d.platformRoas > 0 && (
        <div className="mb-6 rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-[var(--color-warning)]">Platform ROAS vs TRUE ROAS</h3>
              <p className="mt-1 text-sm text-[var(--color-warning)]">
                Platforms report <strong>{d.platformRoas.toFixed(1)}x ROAS</strong>.
                TRUE ROAS (from CRM): <strong>{d.trueRoas.toFixed(1)}x</strong>.
                {d.overcountFactor > 1 && (
                  <> Platforms over-count by <strong>{d.overcountFactor.toFixed(1)}x</strong>.</>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[var(--color-warning)]">{d.overcountFactor.toFixed(1)}x</p>
              <p className="text-xs text-[var(--color-warning)]/80">overcount</p>
            </div>
          </div>
        </div>
      )}

      {/* Per-platform breakdown */}
      {dashboard && dashboard.byPlatform.length > 0 && (
        <div className="mb-6 grid gap-4 md:grid-cols-3">
          {dashboard.byPlatform.map((p) => (
            <div key={p.platform} className="stagger-item rounded-xl border border-border bg-card p-4">
              <div className="mb-2 flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${PLATFORM_COLORS[p.platform] ?? "bg-[var(--color-muted-foreground)]"}`} />
                <h4 className="font-medium">{PLATFORM_NAMES[p.platform] ?? p.platform}</h4>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Spend:</span> {formatMoney(p.spendCents, "EUR")}</div>
                <div><span className="text-muted-foreground">TRUE ROAS:</span> {p.trueRoas.toFixed(1)}x</div>
                <div><span className="text-muted-foreground">Leads:</span> {p.leads}</div>
                <div><span className="text-muted-foreground">CPL:</span> {formatMoney(p.cpl, "EUR")}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-6 rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-medium">Budget Optimizer</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Recommendation rail built on mounted TRUE ROAS campaign data and the existing optimizer contract.
            </p>
          </div>
          <a href="/analytics/ads/budget" className="text-sm font-medium text-primary hover:underline">
            Open optimizer
          </a>
        </div>

        {recommendations.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
            Insufficient campaign data for optimizer recommendations yet. Once campaigns accumulate spend and TRUE ROAS history, increase, decrease, and pause guidance will appear here.
          </div>
        ) : (
          <div className="space-y-3">
            {recommendations.map((recommendation) => (
              <div key={recommendation.id} className="stagger-item flex flex-col gap-3 rounded-lg border border-border/70 bg-background p-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getActionBadgeClass(recommendation.action)}`}>
                      {recommendation.actionLabel}
                    </span>
                    <span className="text-sm font-medium">{recommendation.name}</span>
                    <span className="text-xs text-muted-foreground">({PLATFORM_NAMES[recommendation.platform] ?? recommendation.platform})</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{recommendation.reason}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span>TRUE ROAS: {recommendation.trueRoas.toFixed(1)}x</span>
                    <span>Spend: {formatMoney(recommendation.spendCents, "EUR")}</span>
                  </div>
                </div>
                <a
                  href={recommendation.editorHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
                >
                  Review in platform
                </a>
              </div>
            ))}
          </div>
        )}

        {optimizer?.aiRecommendation && (
          <div className="mt-4 rounded-lg border border-[var(--color-ai)]/20 bg-[var(--color-ai)]/5 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-ai)]">Optimizer guidance</p>
            <p className="mt-1 text-sm text-[var(--color-foreground)]">{optimizer.aiRecommendation}</p>
          </div>
        )}
      </div>

      {/* D8: Anomalies */}
      {anomalies.length > 0 && (
        <div className="mb-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="font-medium">Anomalies Detected</h3>
            <a href="/analytics/ads/alerts" className="text-sm font-medium text-primary hover:underline">
              Open alert center
            </a>
          </div>
          <div className="space-y-2">
            {anomalies.map((a, i) => (
              <div key={i} className={`stagger-item rounded-lg border p-3 ${a.severity === "critical" ? "border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5" : "border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${a.severity === "critical" ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"}`}>{a.type.replace(/_/g, " ")}</span>
                    <span className="ml-2 text-sm font-medium">{a.campaignName}</span>
                    <span className="ml-1 text-xs text-muted-foreground">({PLATFORM_NAMES[a.platform]})</span>
                  </div>
                </div>
                <p className="mt-1 text-sm">{a.message}</p>
                <p className="mt-1 text-xs text-primary">AI: {a.recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* D7: Top Campaigns */}
      <h3 className="mb-3 font-medium">Top Campaigns by TRUE ROAS</h3>
      <div className="rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-4 py-3 font-medium">Campaign</th>
              <th className="px-4 py-3 font-medium">Platform</th>
              <th className="px-4 py-3 font-medium">Spend</th>
              <th className="px-4 py-3 font-medium">Leads</th>
              <th className="px-4 py-3 font-medium">Revenue</th>
              <th className="px-4 py-3 font-medium">TRUE ROAS</th>
              <th className="px-4 py-3 font-medium">CPL</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No campaign data yet. Connect platforms and wait for first sync.</td></tr>
            )}
            {campaigns.slice(0, 10).map((c) => (
              <tr key={c.id} className="stagger-item border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3">
                  <a href={`/analytics/ads/campaigns/${c.id}`} className="font-medium text-primary hover:underline">{c.name}</a>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-white ${PLATFORM_COLORS[c.platform] ?? "bg-[var(--color-muted-foreground)]"}`}>
                    {PLATFORM_NAMES[c.platform]}
                  </span>
                </td>
                <td className="px-4 py-3">{formatMoney(c.spendCents, "EUR")}</td>
                <td className="px-4 py-3">{c.leads}</td>
                <td className="px-4 py-3 font-medium">{formatMoney(c.trueRevenueCents, "EUR")}</td>
                <td className="px-4 py-3">
                  <span className={c.trueRoas >= 3 ? "text-[var(--color-success)] font-bold" : c.trueRoas >= 1 ? "text-[var(--color-primary)]" : "text-[var(--color-destructive)] font-bold"}>
                    {c.trueRoas.toFixed(1)}x
                  </span>
                </td>
                <td className="px-4 py-3">{formatMoney(c.cpl, "EUR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`stagger-item rounded-xl border border-border p-4 ${highlight ? "bg-primary/5" : "bg-card"}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold ${highlight ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}
